import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

export interface CapturedVideo {
  file: File;
  previewUrl: string;
}

@Component({
  selector: 'app-video-capture',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './video-capture.html',
})
export class VideoCaptureComponent implements OnChanges, OnDestroy {

  @Input() isOpen = false;
  @Output() videoTaken  = new EventEmitter<CapturedVideo>();
  @Output() cancelled   = new EventEmitter<void>();

  @ViewChild('videoEl')   videoEl!:   ElementRef<HTMLVideoElement>;
  @ViewChild('previewEl') previewEl!: ElementRef<HTMLVideoElement>;

  streamActive     = false;
  isRecording      = false;
  recordedVideoUrl: string | null = null;
  recordedBlob:     Blob   | null = null;
  cameraError:      string | null = null;
  isSwitching      = false;

  recordingDuration = 0;
  private recordingTimer: ReturnType<typeof setInterval> | null = null;

  availableCameras:   MediaDeviceInfo[] = [];
  selectedCameraId:   string | null     = null;
  hasMultipleCameras  = false;
  activeFacingMode:   'user' | 'environment' = 'user';

  private activeStream:   MediaStream   | null = null;
  private mediaRecorder:  MediaRecorder | null = null;
  private recordedChunks: Blob[]               = [];

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  // ════════════════════════════════════════════════════════════
  //  LIFECYCLE
  // ════════════════════════════════════════════════════════════

  ngOnChanges(changes: SimpleChanges): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.reset();
        setTimeout(() => this.requestCamera(), 80);
      } else {
        this.stopStream();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopStream();
    this.clearRecordingTimer();
  }

  // ════════════════════════════════════════════════════════════
  //  CAMERA INITIALISATION
  // ════════════════════════════════════════════════════════════

  async requestCamera(): Promise<void> {
    this.cameraError  = null;
    this.streamActive = false;
    this.cdr.detectChanges();

    if (!navigator.mediaDevices?.getUserMedia) {
      this.cameraError = 'Your browser does not support camera access. Please try Chrome, Firefox, or Safari.';
      this.cdr.detectChanges();
      return;
    }

    try {
      await this.enumerateCameras();

      const constraints = this.buildConstraints();
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      this.activeStream = stream;

      const videoTrack = stream.getVideoTracks()[0] ?? null;
      this.activeFacingMode = this.resolveFacingModeFromTrack(videoTrack, this.activeFacingMode);

      const video = this.videoEl?.nativeElement;
      if (!video) return;
      // Must set muted in JS — Angular's template `muted` attr is ignored when
      // srcObject is assigned programmatically, causing mic audio to echo back.
      video.muted  = true;
      video.volume = 0;
      video.srcObject = stream;
      await video.play();

      this.streamActive = true;
      this.isSwitching  = false;

      await this.enumerateCameras();
      this.cdr.detectChanges();
    } catch (err: any) {
      this.streamActive = false;
      this.isSwitching  = false;
      this.cameraError  = this.translateCameraError(err);
      this.cdr.detectChanges();
    }
  }

  private buildConstraints(): MediaStreamConstraints {
    const video: MediaTrackConstraints = {
      width:  { ideal: 1920 },
      height: { ideal: 1080 },
    };

    if (this.selectedCameraId) {
      video.deviceId = { exact: this.selectedCameraId };
    } else {
      video.facingMode = { ideal: this.activeFacingMode };
    }

    return { video, audio: true };
  }

  private async enumerateCameras(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.availableCameras   = devices.filter(d => d.kind === 'videoinput');
      this.hasMultipleCameras = this.availableCameras.length > 1;

      if (!this.selectedCameraId && this.availableCameras.length > 0) {
        const front = this.availableCameras.find(c => {
          const l = c.label.toLowerCase();
          return l.includes('front') || l.includes('selfie') || l.includes('user');
        });
        this.selectedCameraId = (front ?? this.availableCameras[0]).deviceId;
      }
    } catch { /* enumeration failed — not critical */ }
  }

  private resolveFacingModeFromTrack(
    track:    MediaStreamTrack | null,
    fallback: 'user' | 'environment',
  ): 'user' | 'environment' {
    if (!track) return fallback;
    try {
      const settings = track.getSettings() as any;
      if (settings?.facingMode === 'user')        return 'user';
      if (settings?.facingMode === 'environment') return 'environment';
    } catch { /* getSettings not supported */ }
    const label = (track.label ?? '').toLowerCase();
    if (label.includes('front') || label.includes('selfie') || label.includes('user')) return 'user';
    if (label.includes('back')  || label.includes('rear')   || label.includes('environment')) return 'environment';
    return fallback;
  }

  get isFrontCamera(): boolean {
    return this.activeFacingMode === 'user';
  }

  private translateCameraError(err: any): string {
    const name = err?.name ?? '';
    if (name === 'NotAllowedError'    || name === 'PermissionDeniedError')
      return 'Camera/microphone permission was denied. Please allow access in your browser settings and try again.';
    if (name === 'NotFoundError'      || name === 'DevicesNotFoundError')
      return 'No camera was found on this device.';
    if (name === 'NotReadableError'   || name === 'TrackStartError')
      return 'Camera is in use by another app. Please close other apps and try again.';
    if (name === 'SecurityError')
      return 'Camera access requires a secure connection (HTTPS).';
    return 'Could not access camera. Please check your device settings and try again.';
  }

  // ════════════════════════════════════════════════════════════
  //  CAMERA SWITCH
  // ════════════════════════════════════════════════════════════

  async switchCamera(): Promise<void> {
    if (this.isSwitching || this.isRecording) return;
    this.isSwitching = true;
    this.cdr.detectChanges();

    const nextFacing: 'user' | 'environment' =
      this.activeFacingMode === 'environment' ? 'user' : 'environment';

    const match = this.availableCameras.find(c => {
      const l = c.label.toLowerCase();
      if (nextFacing === 'user')        return l.includes('front') || l.includes('selfie') || l.includes('user');
      if (nextFacing === 'environment') return l.includes('back')  || l.includes('rear')   || l.includes('environment');
      return false;
    });

    this.activeFacingMode = nextFacing;
    this.selectedCameraId = match?.deviceId ?? null;
    this.stopStream();
    await this.requestCamera();
  }

  // ════════════════════════════════════════════════════════════
  //  RECORDING
  // ════════════════════════════════════════════════════════════

  startRecording(): void {
    if (!this.streamActive || !this.activeStream || this.isRecording) return;

    this.recordedChunks  = [];
    this.recordedVideoUrl = null;
    this.recordedBlob    = null;

    const mimeType = this.getSupportedMimeType();
    const options: MediaRecorderOptions = mimeType ? { mimeType } : {};

    try {
      this.mediaRecorder = new MediaRecorder(this.activeStream, options);
    } catch {
      this.mediaRecorder = new MediaRecorder(this.activeStream);
    }

    this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) this.recordedChunks.push(e.data);
    };

    this.mediaRecorder.onstop = () => {
      const mimeUsed = this.mediaRecorder?.mimeType || 'video/webm';
      this.recordedBlob    = new Blob(this.recordedChunks, { type: mimeUsed });
      this.recordedVideoUrl = URL.createObjectURL(this.recordedBlob);
      this.cdr.detectChanges();
    };

    this.mediaRecorder.start(100);
    this.isRecording      = true;
    this.recordingDuration = 0;

    this.recordingTimer = setInterval(() => {
      this.recordingDuration++;
      this.cdr.detectChanges();
    }, 1000);

    this.cdr.detectChanges();
  }

  stopRecording(): void {
    if (!this.isRecording || !this.mediaRecorder) return;
    this.mediaRecorder.stop();
    this.isRecording = false;
    this.clearRecordingTimer();
    this.cdr.detectChanges();
  }

  private getSupportedMimeType(): string {
    const types = [
      'video/mp4;codecs=h264,aac',
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    for (const type of types) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) return type;
    }
    // Always return a concrete default — never return '' which causes an
    // empty MIME type on the resulting File and fails server-side validation
    return 'video/webm';
  }

  get formattedDuration(): string {
    const m = Math.floor(this.recordingDuration / 60);
    const s = this.recordingDuration % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  private clearRecordingTimer(): void {
    if (this.recordingTimer !== null) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
  }

  retakeVideo(): void {
    if (this.recordedVideoUrl) {
      URL.revokeObjectURL(this.recordedVideoUrl);
      this.recordedVideoUrl = null;
    }
    this.recordedBlob     = null;
    this.recordingDuration = 0;
    this.cdr.detectChanges();
  }

  useVideo(): void {
    if (!this.recordedBlob || !this.recordedVideoUrl) return;

    // Determine the real MIME type — blob.type can be empty on some browsers/servers
    // when MediaRecorder fell back to its internal default (e.g. Chromium on Linux).
    // Always resolve to a concrete type so the server's MIME validation passes.
    let mimeType = this.recordedBlob.type || '';
    if (!mimeType) {
      // No type set — default to webm which is universally produced by MediaRecorder
      mimeType = 'video/webm';
    }
    // Strip codec params for the extension lookup (e.g. 'video/webm;codecs=vp8' → 'webm')
    const baseMime = mimeType.split(';')[0].trim();
    const ext = baseMime.includes('mp4') ? 'mp4' : 'webm';

    // Re-create the Blob with an explicit type so the File inherits it correctly
    const typedBlob = new Blob([this.recordedBlob], { type: baseMime });
    const file = new File([typedBlob], `video-${Date.now()}.${ext}`, { type: baseMime });
    const previewUrl = this.recordedVideoUrl;
    // Don't revoke — parent needs the URL for the media preview
    this.recordedVideoUrl = null;
    this.videoTaken.emit({ file, previewUrl });
    this.stopStream();
  }

  onCancel(): void {
    this.stopStream();
    this.cancelled.emit();
  }

  // ════════════════════════════════════════════════════════════
  //  HELPERS
  // ════════════════════════════════════════════════════════════

  private stopStream(): void {
    if (this.isRecording) {
      this.mediaRecorder?.stop();
      this.isRecording = false;
      this.clearRecordingTimer();
    }
    if (this.activeStream) {
      this.activeStream.getTracks().forEach(t => t.stop());
      this.activeStream = null;
    }
    const video = this.videoEl?.nativeElement;
    if (video) video.srcObject = null;
    this.streamActive = false;
    this.cdr.detectChanges();
  }

  private reset(): void {
    this.cameraError      = null;
    this.streamActive     = false;
    this.isRecording      = false;
    this.isSwitching      = false;
    this.recordingDuration = 0;
    if (this.recordedVideoUrl) {
      URL.revokeObjectURL(this.recordedVideoUrl);
      this.recordedVideoUrl = null;
    }
    this.recordedBlob     = null;
    this.recordedChunks   = [];
    this.activeFacingMode = 'user';
    this.selectedCameraId = null;
  }
}